import { useSelector } from 'react-redux';
import { Redirect, Route, RouteChildrenProps, Switch } from 'react-router';
import { CircularProgress, Stack } from '@rossum/ui/material';
import QueueAccess from '../../../containers/Queue/containers/QueueAccess/QueueAccess';
import { QueueEmails } from '../../../containers/Queue/containers/QueueEmails';
import { QueueBasicSettings } from '../../../containers/Queue/containers/QueueSettings';
import { canManageQueueSelector } from '../../../redux/modules/user/selectors';
import { HelmetComponent } from '../../../routes/HelmetComponent';
import { State } from '../../../types/state';
import { useQueueWithWorkspace } from '../../queues/hooks/useQueueWithWorkspace';
import { SchemaEditorPage } from '../../schema-editor/SchemaEditor';
import { AutomationPage } from '../automation/AutomationPage';
import { ConfigMapPage } from '../config-map/ConfigMapPage';
import { EditModePage } from '../edit-mode/EditModePage';
import { QueueSettingsPage } from '../fields/constants';
import { useVisibleQueueSettingsPages } from '../hooks/useVisibleQueueSettingsPages';
import { FieldsRoute } from './FieldsRoute';
import { RulesRoute } from './RulesRoute';

export const QueueSettingsRoute = (
  props: RouteChildrenProps<{ queueId: string }>
) => {
  const { match, location } = props;

  const queueId = Number(match?.params.queueId);
  const { queue, queueIsLoading, workspace, workspaceIsLoading } =
    useQueueWithWorkspace({
      queueId,
    });

  const canManage = useSelector((state: State) =>
    canManageQueueSelector(state, queue?.url)
  );

  const visiblePages = useVisibleQueueSettingsPages();

  const canSeePage = (page: QueueSettingsPage) => visiblePages.includes(page);

  // Derive the default redirect from the same visible-pages list that renders
  // the tabs, so it can't drift from the first visible tab.
  const defaultPage: QueueSettingsPage = visiblePages[0] ?? 'basic';

  if (!match) {
    return null;
  }

  if (!queue || !workspace) {
    if (queueIsLoading || workspaceIsLoading) {
      return (
        <Stack
          sx={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Stack>
      );
    }

    return <Redirect to="/documents" />;
  }

  if (!canManage) {
    return <Redirect to="/documents" />;
  }

  return (
    <>
      <HelmetComponent
        dynamicName={queue.name}
        translationKey="features.routes.pageTitles.queueSettings"
      />
      <Switch>
        {canSeePage('schema') && (
          <Route path={`${match.path}/schema`}>
            <SchemaEditorPage queue={queue} workspace={workspace} />
          </Route>
        )}
        <Route>
          <Switch location={location}>
            {canSeePage('basic') && (
              <Route path={`${match.path}/basic`}>
                <QueueBasicSettings {...props} match={match} />
              </Route>
            )}
            {canSeePage('fields') && (
              <Route path={`${match.path}/fields`}>
                <FieldsRoute
                  {...props}
                  queue={queue}
                  workspaceName={workspace.name}
                />
              </Route>
            )}
            {canSeePage('rules') && (
              <Route path={`${match.path}/rules`}>
                <RulesRoute
                  currentQueue={queue}
                  workspaceName={workspace.name}
                  {...props}
                />
              </Route>
            )}
            {canSeePage('emails') && (
              <Route path={`${match.path}/emails`}>
                <QueueEmails
                  {...props}
                  match={match}
                  workspaceName={workspace.name}
                />
              </Route>
            )}
            {canSeePage('automation') && (
              <Route path={`${match.path}/automation`}>
                <AutomationPage
                  selectedQueue={queue}
                  parentPath={match.url}
                  workspaceName={workspace.name}
                />
              </Route>
            )}
            {canSeePage('access') && (
              <Route path={`${match.path}/access`}>
                <QueueAccess
                  selectedQueue={queue}
                  workspaceName={workspace.name}
                  {...props}
                />
              </Route>
            )}
            {canSeePage('edit') && (
              <Route path={`${match.path}/edit`}>
                <EditModePage
                  queue={queue}
                  parentPath={match.url}
                  workspaceName={workspace.name}
                />
              </Route>
            )}
            {canSeePage('overview') && (
              <Route path={`${match.path}/overview`}>
                <ConfigMapPage
                  queue={queue}
                  parentPath={match.url}
                  workspaceName={workspace.name}
                />
              </Route>
            )}
            <Route
              path={`${match.path}/`}
              render={() => (
                <Redirect
                  to={{
                    pathname: `${match.url}/${defaultPage}`,
                    state: location.state,
                  }}
                />
              )}
            />
          </Switch>
        </Route>
      </Switch>
    </>
  );
};
